/**
 * ReportsTabCN — scope-agnostic Reports tab for entity detail screens.
 *
 * Hosts the Generate panel + history list + preview modal for any scope
 * (project / location / portfolio). Mirrors the evaluation
 * `ReportWorkspace.tsx` UI but is parameterized by `{scope, entityId}` so the
 * same component plugs into all four detail screens.
 *
 * Linear: MYD-339 (Phase 2 — initial use for projects).
 */

import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FileText, Plus, Loader2 } from 'lucide-react'

import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Select } from 'components/ui/select'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Textarea } from 'components/ui/textarea'
import { cn } from 'lib/utils'

import {
	fetchReports,
	listReportTemplates,
	generateReport,
	deleteReport,
	clearLastGenerated,
} from 'slices/reportSlice'
import {
	sourceKey,
	type ReportFormat,
	type ReportRow,
	type ReportScope,
	type TemplateDescriptor,
} from 'services/reports/reportService'
import { toast as toastApi } from 'components/ui/toast-store'

import ReportHistory from 'screens/evaluations/components/reports/ReportHistory'
import ReportPreview from 'screens/evaluations/components/reports/ReportPreview'

interface ReportsTabCNProps {
	scope: ReportScope
	entityId: string
	heading?: string
	subhead?: string
}

const ALL_FORMATS: ReportFormat[] = ['PDF', 'DOCX', 'HTML', 'XLSX']

const ReportsTabCN = ({
	scope,
	entityId,
	heading = 'Reports',
	subhead = 'Pick a template, choose a format, and generate. Generated artefacts land in the history below and on the Documents tab.',
}: ReportsTabCNProps) => {
	const dispatch = useDispatch()
	const key = sourceKey(scope, entityId)

	const reports: ReportRow[] = useSelector(
		(state: any) => state.reports?.bySource?.[key] || [],
	)
	const templates: TemplateDescriptor[] = useSelector(
		(state: any) => state.reports?.templatesBySource?.[key] || [],
	)
	const templatesLoaded: boolean = useSelector(
		(state: any) => Boolean(state.reports?.templatesLoadedBySource?.[key]),
	)
	const isGenerating: boolean = useSelector(
		(state: any) => Boolean(state.reports?.isGenerating),
	)
	const lastGeneratedId: string | null = useSelector(
		(state: any) => state.reports?.lastGeneratedId ?? null,
	)

	const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('')
	const [selectedFormat, setSelectedFormat] = useState<ReportFormat>('PDF')
	const [previewReport, setPreviewReport] = useState<ReportRow | null>(null)
	// MYD-343: partner-packet template inputs. Kept in component state so
	// switching templates resets them naturally via the useEffect below.
	const [recipientName, setRecipientName] = useState('')
	const [recipientCompany, setRecipientCompany] = useState('')
	const [recipientEmail, setRecipientEmail] = useState('')
	const [senderNote, setSenderNote] = useState('')
	// Notifications go through the shared toast store (MYD-561), titled as before.
	const showToast = ({ message, variant }: { message: string; variant: 'success' | 'destructive' }) =>
		variant === 'success' ? toastApi.success(message, { title: 'Success' }) : toastApi.error(message, { title: 'Error' })

	useEffect(() => {
		// @ts-ignore — thunks
		dispatch(fetchReports({ scope, entityId }))
		// @ts-ignore — thunks
		dispatch(listReportTemplates({ scope, entityId }))
	}, [dispatch, scope, entityId])

	useEffect(() => {
		if (!selectedTemplateKey && templates.length > 0) {
			setSelectedTemplateKey(templates[0].key)
		}
	}, [templates, selectedTemplateKey])

	const selectedTemplate = useMemo(
		() => templates.find((t) => t.key === selectedTemplateKey),
		[templates, selectedTemplateKey],
	)

	useEffect(() => {
		if (!selectedTemplate) return
		if (!selectedTemplate.supportedFormats.includes(selectedFormat)) {
			setSelectedFormat(selectedTemplate.supportedFormats[0])
		}
	}, [selectedTemplate, selectedFormat])

	// Reset partner-packet inputs whenever we switch templates so a leftover
	// recipient name doesn't carry over to a different report.
	useEffect(() => {
		setRecipientName('')
		setRecipientCompany('')
		setRecipientEmail('')
		setSenderNote('')
	}, [selectedTemplateKey])

	const isPartnerPacket = selectedTemplate?.aggregatorMode === 'partner-packet'
	const requiredArgsFilled = (selectedTemplate?.requiredTemplateArgs ?? []).every((argName) => {
		if (argName === 'recipientName') return recipientName.trim().length > 0
		// Future required-arg names can be wired in here.
		return true
	})

	// Auto-open preview when a generate succeeds (parallels ReportWorkspace).
	const lastOpenedIdRef = useRef<string | null>(null)
	useEffect(() => {
		if (!lastGeneratedId) return
		if (lastOpenedIdRef.current === lastGeneratedId) return
		const row = reports.find((r) => r._id === lastGeneratedId)
		if (!row || row.status !== 'READY') return
		lastOpenedIdRef.current = lastGeneratedId
		setPreviewReport(row)
	}, [lastGeneratedId, reports])

	const handleGenerate = useCallback(async () => {
		if (!selectedTemplateKey) return
		const templateArgs = isPartnerPacket
			? {
				recipientName: recipientName.trim(),
				recipientCompany: recipientCompany.trim() || undefined,
				recipientEmail: recipientEmail.trim() || undefined,
				senderNote: senderNote.trim() || undefined,
			}
			: undefined
		// @ts-ignore — thunks
		const result: any = await dispatch(
			generateReport({
				scope,
				entityId,
				templateKey: selectedTemplateKey,
				format: selectedFormat,
				templateArgs,
			}),
		)
		if (result?.error) {
			showToast({
				message: result.payload || 'Report generation failed',
				variant: 'destructive',
			})
		} else {
			showToast({
				message: `${selectedTemplate?.label ?? 'Report'} generated.`,
				variant: 'success',
			})
		}
	}, [
		dispatch,
		scope,
		entityId,
		selectedTemplateKey,
		selectedFormat,
		selectedTemplate,
		isPartnerPacket,
		recipientName,
		recipientCompany,
		recipientEmail,
		senderNote,
	])

	const handleDelete = useCallback(
		async (reportId: string) => {
			if (!(await toastApi.confirm({ title: 'Delete report', message: 'Delete this report record?' })))
				return
			// @ts-ignore — thunks
			await dispatch(deleteReport({ scope, entityId, reportId }))
			if (reportId === lastGeneratedId) {
				dispatch(clearLastGenerated())
			}
			if (previewReport?._id === reportId) {
				setPreviewReport(null)
			}
		},
		[dispatch, scope, entityId, lastGeneratedId, previewReport],
	)

	const handlePreview = useCallback((report: ReportRow) => {
		setPreviewReport(report)
	}, [])

	const formatChoices: ReportFormat[] = selectedTemplate?.supportedFormats ?? [
		'PDF',
		'DOCX',
		'HTML',
	]

	return (
		<div className="space-y-5">
			<div>
				<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
					<FileText className="w-5 h-5 text-[#0080fe]" />
					{heading}
				</h3>
				<p className="text-sm text-gray-500">{subhead}</p>
			</div>

			{/* Generate panel */}
			<Card>
				<CardHeader className="pb-3">
					<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
						<Plus className="w-4 h-4" />
						New report
					</h4>
				</CardHeader>
				<CardContent>
					{!templatesLoaded ? (
						<div className="flex items-center gap-2 text-sm text-gray-500 py-3">
							<Loader2 className="w-4 h-4 animate-spin" />
							Loading templates…
						</div>
					) : templates.length === 0 ? (
						<div className="text-sm text-gray-500 py-3">
							No templates registered for this entity type yet.
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 md:items-end">
							<div className="space-y-1">
								<label
									htmlFor={`report-template-${scope}`}
									className="text-xs font-medium text-gray-600 uppercase tracking-wide"
								>
									Template
								</label>
								<Select
									id={`report-template-${scope}`}
									value={selectedTemplateKey}
									onChange={(e) => setSelectedTemplateKey(e.target.value)}
								>
									{templates.map((t) => (
										<option key={t.key} value={t.key}>
											{t.label}
										</option>
									))}
								</Select>
								{selectedTemplate?.description && (
									<p className="text-xs text-gray-500">{selectedTemplate.description}</p>
								)}
							</div>

							<div className="space-y-1">
								<label className="text-xs font-medium text-gray-600 uppercase tracking-wide block">
									Format
								</label>
								<div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
									{ALL_FORMATS.map((fmt) => {
										const supported = formatChoices.includes(fmt)
										const active = selectedFormat === fmt
										if (!supported) return null
										return (
											<button
												key={fmt}
												type="button"
												onClick={() => setSelectedFormat(fmt)}
												className={cn(
													'px-3 py-1.5 text-xs font-medium rounded transition-colors',
													active
														? 'bg-[#0080fe] text-white'
														: 'text-gray-600 hover:bg-gray-100',
												)}
											>
												{fmt}
											</button>
										)
									})}
								</div>
							</div>

							<Button
								variant="primary"
								disabled={!selectedTemplateKey || isGenerating || !requiredArgsFilled}
								onClick={handleGenerate}
								className="gap-1.5"
							>
								{isGenerating ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										Generating…
									</>
								) : (
									<>
										<Plus className="w-4 h-4" />
										Generate
									</>
								)}
							</Button>
						</div>
					)}

					{/* MYD-343: partner-packet-specific inputs. Renders below the
					    template/format/generate row so the layout stays clean for
					    standard templates. */}
					{isPartnerPacket && (
						<div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
							<div>
								<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
									Recipient
								</h4>
								<p className="text-xs text-gray-500 mt-0.5">
									Cover-page details. Sender name + email are filled in from your account.
								</p>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
								<div className="space-y-1">
									<Label htmlFor={`packet-name-${scope}`}>
										Name <span className="text-red-500">*</span>
									</Label>
									<Input
										id={`packet-name-${scope}`}
										value={recipientName}
										onChange={(e) => setRecipientName(e.target.value)}
										placeholder="e.g. Jane Doe"
										autoComplete="off"
									/>
								</div>
								<div className="space-y-1">
									<Label htmlFor={`packet-company-${scope}`}>Company</Label>
									<Input
										id={`packet-company-${scope}`}
										value={recipientCompany}
										onChange={(e) => setRecipientCompany(e.target.value)}
										placeholder="Optional"
										autoComplete="off"
									/>
								</div>
								<div className="space-y-1">
									<Label htmlFor={`packet-email-${scope}`}>Email</Label>
									<Input
										id={`packet-email-${scope}`}
										type="email"
										value={recipientEmail}
										onChange={(e) => setRecipientEmail(e.target.value)}
										placeholder="Optional"
										autoComplete="off"
									/>
								</div>
							</div>
							<div className="space-y-1">
								<Label htmlFor={`packet-note-${scope}`}>Sender note</Label>
								<Textarea
									id={`packet-note-${scope}`}
									value={senderNote}
									onChange={(e) => setSenderNote(e.target.value)}
									placeholder="Optional. A short note to the recipient — appears on the cover page."
									rows={3}
								/>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			<ReportHistory
				scope={scope}
				entityId={entityId}
				reports={reports}
				lastGeneratedId={lastGeneratedId}
				onPreview={handlePreview}
				onDelete={handleDelete}
			/>

			<ReportPreview
				open={Boolean(previewReport)}
				onClose={() => setPreviewReport(null)}
				scope={scope}
				entityId={entityId}
				report={previewReport}
			/>

		</div>
	)
}

export default ReportsTabCN
